/**
 * Kanban Account Card Component
 * 
 * Specialized version of AccountCard for use in kanban boards.
 * Displays account information in a card format optimized for kanban layout.
 * 
 * Key Features:
 * - Account detail navigation (click to open account page)
 * - Notes navigation (notes button in bottom action bar)
 * - AI summarization (chat button in bottom action bar sends account notes to AI for analysis)
 * - Keyword highlighting for search results
 * - Type chip display (RLP/Team classification) in bottom action bar
 * - Simplified layout optimized for kanban columns
 * - No delete functionality (kanban is view-only)
 * - No drag-to-chat (handled by kanban drag system)
 * 
 * Layout:
 * - Header: Account name 
 * - Content: Key account details in main area
 * - Footer: Tags and action buttons
 * 
 * Props:
 * - account: Account object with account details and noteCount
 * - keywords: Array of strings for highlighting search matches
 */

import { FC } from 'react';
import { useNavigate } from 'react-router';
import { Account } from '../../models/Account';
import { THEME_CLASSES } from '../../constants/themeConstants';
import { highlightText } from '../../utils/highlightUtils';
import { getAccountCardDisplayFields } from '../../utils/accountUtils';
import { renderTypeChip } from '../../utils/typeChipUtils';
import { useEventBus } from '../../context/EventBusContext';
import { getNotesSummaryPrompt } from '../../utils/summaryUtils';
import { request } from '../../services/dataService';
import { API_ENDPOINTS } from '../../constants/apiEndpoints';

interface KanbanAccountCardProps {
  account: Account;
  keywords: string[];
}

const KanbanAccountCard: FC<KanbanAccountCardProps> = ({ account, keywords }) => {
  const navigate = useNavigate();
  const { publish } = useEventBus();

  const handleNotesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/accounts/${account.id}/notes`);
  };

  const sendNotesToChat = async () => {
    try {
      const notes = await request(API_ENDPOINTS.GET_ACCOUNT_NOTES, { id: account.id });
      if (!notes || notes.length === 0) return;
      const prompt = getNotesSummaryPrompt(notes);
      publish('chat:open', prompt);
    } catch (error) {
      console.error('Failed to fetch account notes:', error);
    }
  };

  const handleChatClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    sendNotesToChat();
  };

  // Get only the most important fields for kanban display
  const displayFields = getAccountCardDisplayFields().slice(0, 4); // Limit to first 4 fields

  return (
    <div
      className={`${THEME_CLASSES.card.background} rounded-xl shadow-lg p-4 border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.hover.full} cursor-pointer`}
      onClick={() => navigate(`/accounts/${account.id}`)}
    >
      <div className="flex flex-col h-full">
        {/* Header - Account name */}
        <div className="mb-3">
          <h3 className={`text-sm font-semibold ${THEME_CLASSES.text.primary}`}>
            {highlightText(account.name, keywords)}
          </h3>
        </div>

        {/* Content - Key details */}
        <div className="text-xs space-y-2 flex-1 mb-3">
          {displayFields.map(field => {
            const value = account[field.key];
            if (field.hideEmpty && !value) {
              return null;
            }

            const displayValue = value ?? '-';
            const needsHighlight = typeof value === 'string';

            return (
              <div key={field.key} className="flex justify-between items-start gap-2">
                <span className={`${THEME_CLASSES.text.secondary} shrink-0 text-xs`}>{field.label}:</span>
                <span className={`${THEME_CLASSES.text.secondary} text-right text-xs ${field.key === 'streetAddress' || field.key === 'mailingAddress' ? 'break-words' : ''}`}>
                  {needsHighlight ? highlightText(displayValue.toString(), keywords) : displayValue}
                </span>
              </div>
            );
          })}
        </div>

        {/* Footer - Tags and actions */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-600">
          <div className="flex items-center gap-1">
            {renderTypeChip({ rlpCompanyId: account.rlpCompanyId, teamId: account.teamId })}
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={handleNotesClick}
              className={`${THEME_CLASSES.button.iconSmall} p-1`}
              title="View notes"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 ${THEME_CLASSES.text.secondary}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </button>
            <button
              onClick={handleChatClick}
              className={`${THEME_CLASSES.button.iconSmall} p-1`}
              title="Ask AI about this account"
              disabled={account.noteCount === 0}
            >
              <svg className={`h-3 w-3 ${THEME_CLASSES.text.secondary}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KanbanAccountCard;